import { httpsCallable } from 'firebase/functions';
import type { OpportunityRoadmapItem, OpportunityRoadmapLevel, OpportunityRoadmapPhase } from '../types';
import { newId } from '../utils/id';
import { functions } from './firebase';

// Keep strictly LONGER than backend timeoutSeconds (180s) so the server can
// finish or return its own error instead of a client-side deadline.
const ROADMAP_CALLABLE_TIMEOUT_MS = 200_000;

type RoadmapItemPayload = {
  phase: OpportunityRoadmapPhase;
  title: string;
  description: string;
  impact: OpportunityRoadmapLevel;
  effort: OpportunityRoadmapLevel;
  timeframe: string;
};

export type GenerateOpportunityRoadmapResponse = {
  roadmap: {
    generatedAt: string;
    model: string;
    summary: string;
    items: RoadmapItemPayload[];
  };
};

export type GeneratedOpportunityRoadmap = {
  generatedAt: string;
  model: string;
  summary: string;
  items: OpportunityRoadmapItem[];
};

export function generateOpportunityRoadmapErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Describe your idea before generating a roadmap.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to generate a roadmap.';
    }
    if (code === 'functions/unavailable') {
      return 'Roadmap service is temporarily unavailable. Try again.';
    }
    if (code === 'functions/deadline-exceeded') {
      return 'Roadmap generation timed out. Try again in a moment.';
    }
  }
  return err instanceof Error ? err.message : 'Roadmap generation failed.';
}

export async function generateOpportunityRoadmap(
  opportunityId: string
): Promise<GeneratedOpportunityRoadmap> {
  const fn = httpsCallable<{ opportunityId: string }, GenerateOpportunityRoadmapResponse>(
    functions,
    'generateOpportunityRoadmap',
    { timeout: ROADMAP_CALLABLE_TIMEOUT_MS }
  );
  const result = await fn({ opportunityId: opportunityId.trim() });
  const { roadmap } = result.data;
  return {
    ...roadmap,
    items: roadmap.items.map((item) => ({ ...item, id: newId(), done: false })),
  };
}
